import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { useScope } from "@/hooks/use-scope";
import {
  listReceivingPolicies, upsertReceivingPolicy, deleteReceivingPolicy,
  previewReceivingPolicy,
} from "@/lib/receiving-policies.functions";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/admin/receiving-policies")({
  component: ReceivingPoliciesAdmin,
});

type Scope = "tenant" | "facility" | "customer";

function ReceivingPoliciesAdmin() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listReceivingPolicies);
  const upsertFn = useServerFn(upsertReceivingPolicy);
  const deleteFn = useServerFn(deleteReceivingPolicy);
  const previewFn = useServerFn(previewReceivingPolicy);

  const { data: policies = [], isLoading } = useQuery({
    queryKey: ["receiving-policies", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const [draft, setDraft] = useState({
    scope: "tenant" as Scope,
    facilityIdInput: facilityId ?? "",
    customerIdInput: "",
    variance: "10",
    sla: "4",
    grace: "2",
    photoRequired: true,
    active: true,
  });

  const save = useMutation({
    mutationFn: () =>
      runServerFn(
        () =>
          upsertFn({
            data: {
              tenantId: tenantId!,
              scope: draft.scope,
              facilityId: draft.scope === "facility" ? draft.facilityIdInput || null : null,
              customerId: draft.scope === "customer" ? draft.customerIdInput || null : null,
              variance_signal_threshold: Number(draft.variance) || 0,
              putaway_sla_hours: Number(draft.sla) || 1,
              asn_overdue_grace_hours: Number(draft.grace) || 0,
              damage_photo_required: draft.photoRequired,
              is_active: draft.active,
            },
          }),
        "Saving policy",
      ),
    onSuccess: () => {
      toast.success("Policy saved");
      qc.invalidateQueries({ queryKey: ["receiving-policies", tenantId] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      runServerFn(
        () => deleteFn({ data: { id, tenantId: tenantId! } }),
        "Deleting policy",
      ),
    onSuccess: () => {
      toast.success("Policy removed");
      qc.invalidateQueries({ queryKey: ["receiving-policies", tenantId] });
    },
  });

  const { data: preview } = useQuery({
    queryKey: ["receiving-policy-preview", tenantId, facilityId],
    queryFn: () =>
      previewFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null } }),
    enabled: !!tenantId,
  });

  if (!tenantId) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        Select a tenant to manage receiving policies.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <PageHeader
        title="Receiving Policies"
        description="Tune variance thresholds, putaway SLAs, ASN overdue grace, and damage-photo requirements. Cascades customer → facility → tenant."
      />

      {preview && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Effective policy for current scope
              </div>
              <div className="text-sm mt-1">
                Source: <Badge variant="secondary">{preview.source}</Badge>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <PreviewStat label="Variance threshold" value={`${preview.variance_signal_threshold}u`} />
              <PreviewStat label="Putaway SLA" value={`${preview.putaway_sla_hours}h`} />
              <PreviewStat label="ASN grace" value={`${preview.asn_overdue_grace_hours}h`} />
              <PreviewStat
                label="Damage photo"
                value={preview.damage_photo_required ? "Required" : "Optional"}
              />
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <div className="font-medium">New / edit policy</div>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label>Scope</Label>
            <Select value={draft.scope} onValueChange={(v: Scope) => setDraft({ ...draft, scope: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant">Tenant</SelectItem>
                <SelectItem value="facility">Facility</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {draft.scope === "facility" && (
            <div className="col-span-3">
              <Label>Facility ID</Label>
              <Input
                value={draft.facilityIdInput}
                onChange={(e) => setDraft({ ...draft, facilityIdInput: e.target.value })}
                placeholder="uuid"
              />
            </div>
          )}
          {draft.scope === "customer" && (
            <div className="col-span-3">
              <Label>Customer ID</Label>
              <Input
                value={draft.customerIdInput}
                onChange={(e) => setDraft({ ...draft, customerIdInput: e.target.value })}
                placeholder="uuid"
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label>Variance threshold (units)</Label>
            <Input value={draft.variance} onChange={(e) => setDraft({ ...draft, variance: e.target.value })} />
          </div>
          <div>
            <Label>Putaway SLA (hours)</Label>
            <Input value={draft.sla} onChange={(e) => setDraft({ ...draft, sla: e.target.value })} />
          </div>
          <div>
            <Label>ASN overdue grace (hours)</Label>
            <Input value={draft.grace} onChange={(e) => setDraft({ ...draft, grace: e.target.value })} />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Damage photo required</Label>
            <Switch
              checked={draft.photoRequired}
              onCheckedChange={(v) => setDraft({ ...draft, photoRequired: v })}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save policy
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b text-sm font-medium">Configured policies</div>
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : (policies as any[]).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No policies configured. Defaults apply.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Scope</th>
                <th className="text-left px-4 py-2">Target</th>
                <th className="text-right px-4 py-2">Variance</th>
                <th className="text-right px-4 py-2">Putaway SLA</th>
                <th className="text-right px-4 py-2">ASN grace</th>
                <th className="text-center px-4 py-2">Damage photo</th>
                <th className="text-center px-4 py-2">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(policies as any[]).map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-4 py-2"><Badge variant="outline">{p.scope}</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {p.scope === "tenant"
                      ? "—"
                      : p.scope === "facility"
                        ? p.facility_id
                        : p.customer_id}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.variance_signal_threshold}u</td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.putaway_sla_hours}h</td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.asn_overdue_grace_hours}h</td>
                  <td className="px-4 py-2 text-center">{p.damage_photo_required ? "Yes" : "No"}</td>
                  <td className="px-4 py-2 text-center">{p.is_active ? "Yes" : "No"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove.mutate(p.id)}
                      disabled={remove.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function PreviewStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}